/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { CalendarDate, getDayOfWeek, toZoned } from '@internationalized/date';

import { localToday } from 'sarsaparilla';

/** String literal type representing days of the week */
export type DayOfWeek =
    'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

/** Options for configuring the getNextWeekend function */
export interface GetNextWeekendOptions {
    /** Day of week for weekend start. Default: 'friday' */
    weekendStartDay?: DayOfWeek;
    /** Day of week for weekend end. Default: 'sunday' */
    weekendEndDay?: DayOfWeek;
    /** If true, today can be the start of the weekend range. If false, skip to next week. Default: true */
    allowSameDayStart?: boolean;
    /** @internal For testing only - overrides localToday */
    fromDate?: CalendarDate;
}

/** Mapping from day name strings to numeric day values (Sunday = 0, Saturday = 6) */
const DAY_TO_NUMBER: Record<DayOfWeek, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

/**
 * Returns a tuple of JavaScript Date objects in UTC time representing the next weekend range.
 * By default returns the next Friday and Sunday.
 *
 * @param options - Configuration options for the weekend calculation
 * @returns A tuple of [startDate, endDate] as JavaScript Date objects in UTC
 */
export function getNextWeekend(options: GetNextWeekendOptions = {}): [Date, Date] {
    // Extract options with defaults
    const {
        weekendStartDay = 'friday',
        weekendEndDay = 'sunday',
        allowSameDayStart = true,
        fromDate = localToday,
    } = options;

    // Convert string day names to numeric values
    const startDayNumber = DAY_TO_NUMBER[weekendStartDay];
    const endDayNumber = DAY_TO_NUMBER[weekendEndDay];

    // Get the current day of week (Sunday = 0, Monday = 1, ..., Saturday = 6)
    const currentDay = getDayOfWeek(fromDate, 'en-US');

    // Calculate days until the weekend start day
    let daysUntilStart = (startDayNumber - currentDay + 7) % 7;

    // If today is the start day and same-day start is not allowed, skip to next week
    if (daysUntilStart === 0 && !allowSameDayStart) {
        daysUntilStart = 7;
    }

    // Calculate the offset from start day to end day
    const daysFromStartToEnd = (endDayNumber - startDayNumber + 7) % 7;

    // Calculate the actual dates
    const startDate = fromDate.add({ days: daysUntilStart });
    const endDate = fromDate.add({ days: daysUntilStart + daysFromStartToEnd });

    return [toZoned(startDate, 'UTC').toDate(), toZoned(endDate, 'UTC').toDate()];
}
